import { useEffect, useRef, useState, type FormEvent } from 'react';
import { HelpLink } from './DirectorHelp.tsx';
import {
  AD_EVERY_LIMITS,
  AD_EVERY_PRESETS,
  AD_FRAME_DURATIONS,
  AD_FRAME_LOOKS,
  AD_FRAME_SIZES,
  AD_SCHEDULE_MODES,
  AD_SNOOZE_MS,
  AD_WARN_MS,
  BANNER_EDGES,
  BANNER_STYLES,
  CORNER_SLOTS,
  LSHAPE_CORNERS,
  LSHAPE_EVERY_PRESETS,
  LSHAPE_FOR_OPTIONS,
  LSHAPE_IMAGE_LIMITS,
  LSHAPE_IMAGE_STYLES,
  MATERIAL_USES,
  MAX_MATERIALS,
  MAX_SPOT_SECONDS,
  MAX_SPOT_UPLOAD_BYTES,
  MAX_CTA,
  MAX_LINK,
  MAX_TAGLINE,
  ROTATE_LIMITS,
  SPONSOR_PLATES,
  WIDGET_SIZES,
  bannerHasContent,
  bannerImages,
  breakSpots,
  formatSpotTime,
  lshapeCycleMs,
  lshapeItemAt,
  lshapeItems,
  lshapeMaterials,
  lshapePosition,
  normalizeLink,
  rotatingLogos,
  sponsorLogo,
  useFits,
  type AdDue,
  type AdFrame,
  type AdFrameKind,
  type AdFrameSize,
  type AdLShapeDue,
  type AdScheduleMode,
  type LShapeItem,
  type AdsState,
  type ClientMessage,
  type MaterialUse,
  type SponsorMaterial,
  type CornerSlot,
  type OutputState,
  type Sponsor,
  type WidgetSize,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';
import { fileToDataUrl, materialImagePreset } from '../lib/images.ts';
import { useLogoColor } from '../lib/sponsorColor.ts';
import { keyLabel } from '../lib/shortcuts.ts';
import { AdFrameLayer, adFrameChrome, type AdBreakView, type AdFrameView } from './Ads.tsx';

/** Ob der Block offen ist, merkt sich der Browser -- wie die Abschnitte im Ticker-Pult. */
const OPEN_KEY = 'rabbitfuel:regie-werbung';

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Sendezeit bis zur naechsten Pause nach Plan, von hier aus gesehen. Sie
 * laeuft nur, solange gesendet wird -- sonst steht sie (`AdDue.counting`).
 */
function dueLeft(due: AdDue, receivedAt: number): number {
  return due.counting ? Math.max(0, due.inMs - (performance.now() - receivedAt)) : due.inMs;
}

/** Rendert im Takt neu, solange `on` gilt -- fuer Countdowns. */
function useTick(on: boolean, ms: number) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!on) return;
    const timer = window.setInterval(() => tick((n) => n + 1), ms);
    return () => window.clearInterval(timer);
  }, [on, ms]);
}

/** Was gerade mit dem Spot eines Sponsors passiert. */
type Upload = { phase: 'upload'; share: number } | { phase: 'convert' };

/**
 * Laenge eines Videos, gemessen im Browser. Nur der Notnagel fuer einen
 * Server ohne ffmpeg -- und die fruehe Warnung, bevor 150 MB hochgehen, die
 * der Server dann ablehnt. `null`, wenn der Browser das Format nicht kennt
 * (HEVC in Chrome unter Windows): dann entscheidet der Server.
 */
function measureVideo(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const done = (ms: number | null) => {
      window.clearTimeout(timer);
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(ms);
    };
    const timer = window.setTimeout(() => done(null), 8000);
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration * 1000 : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}

/**
 * Werbung im Regie-Pult: Sponsorenliste, Logo im Bild, Werbepause.
 *
 * Wie „Hintergrund & Bild" ein eigener Block zum Aufklappen. Alles, was hier
 * geschaltet wird, liegt auf dem Server -- Logo und Pause sieht also jeder
 * Zuschauer und der RTMP-Ausgang gleichzeitig, und ein Reload der Regie
 * aendert daran nichts.
 */
export function AdDesk({
  slug,
  pin,
  ads,
  receivedAt,
  ad,
  output,
  send,
  onBeforeBreak,
}: {
  slug: string;
  pin: string | undefined;
  ads: AdsState;
  /** Wann `ads` ankam -- ab da zaehlt der Zeitplan hier herunter. */
  receivedAt: number;
  ad: AdBreakView;
  output: OutputState;
  send: (msg: ClientMessage) => void;
  /** Laeuft im Klick auf „Werbepause" -- dort darf der Browser Ton freigeben. */
  onBeforeBreak?: () => void;
}) {
  const [open, setOpen] = useState(readOpen);
  const [uploads, setUploads] = useState<Record<string, Upload>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [spotFile, setSpotFile] = useState<File | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0');
    } catch {
      /* Privater Modus -- dann eben nur bis zum Neuladen. */
    }
  };

  const fail = (err: unknown) => setError(err instanceof Error ? err.message : 'Hat nicht geklappt.');

  /** Video hochladen -- mit den Haekchen, die es gleich bekommen soll. */
  const uploadVideo = async (sponsorId: string, file: File, uses: MaterialUse[] = []) => {
    setError(null);
    if (file.size > MAX_SPOT_UPLOAD_BYTES) return setError('Das Video ist größer als 200 MB.');
    const measured = await measureVideo(file);
    if (measured && measured > MAX_SPOT_SECONDS * 1000 + 500) {
      return setError(`Ein Video darf höchstens ${MAX_SPOT_SECONDS / 60} Minuten lang sein.`);
    }
    setUploads((u) => ({ ...u, [sponsorId]: { phase: 'upload', share: 0 } }));
    try {
      await api.uploadSponsorVideo(slug, pin, sponsorId, file, measured, uses, (share) =>
        setUploads((u) => ({ ...u, [sponsorId]: share >= 1 ? { phase: 'convert' } : { phase: 'upload', share } })),
      );
    } catch (err) {
      fail(err);
    } finally {
      setUploads(({ [sponsorId]: _done, ...rest }) => rest);
    }
  };

  const addImage = async (sponsorId: string, file: File) => {
    setError(null);
    try {
      const image = await fileToDataUrl(file, materialImagePreset(file));
      await api.addSponsorImage(slug, pin, sponsorId, { image, label: file.name.slice(0, 80) });
    } catch (err) {
      fail(err);
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const logo = logoFile ? await fileToDataUrl(logoFile, materialImagePreset(logoFile)) : null;
      const { ads: next } = await api.createSponsor(slug, pin, { name: name.trim(), logo });
      const created = next.sponsors.at(-1);
      setName('');
      setLogoFile(null);
      const spot = spotFile;
      setSpotFile(null);
      formRef.current?.reset();
      // Der Spot geht danach hoch -- das Anlegen soll nicht auf ihn warten.
      if (created && spot) void uploadVideo(created.id, spot, ['break', 'lshape']);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sponsor: Sponsor) => {
    if (!window.confirm(`„${sponsor.name}" mit allen Bildern und Videos entfernen?`)) return;
    setError(null);
    await api.deleteSponsor(slug, pin, sponsor.id).catch(fail);
  };

  const removeMaterial = async (sponsor: Sponsor, material: SponsorMaterial) => {
    const what = material.kind === 'video' ? 'Video' : 'Bild';
    if (!window.confirm(`${what} „${material.label}" von „${sponsor.name}" löschen?`)) return;
    setError(null);
    await api.deleteMaterial(slug, pin, sponsor.id, material.id).catch(fail);
  };

  const logos = rotatingLogos(ads);
  // Eine Botschaft laeuft auf demselben Weg, ist aber keine Werbepause -- die zeigt der Block „Botschaften".
  const running = ad.active && !ad.active.clip ? ad.active : null;
  const frame = ads.frame;

  return (
    <section className={`deck${open ? '' : ' is-collapsed'}`}>
      <header className="deck__head">
        <button type="button" className="deck__toggle" onClick={toggle} aria-expanded={open}>
          {open ? '▾' : '▸'} Werbung
        </button>
        <HelpLink topic="werbung" />
        {/* Auch zugeklappt soll die Regie sehen, wann die naechste Pause kommt. */}
        {!running && ads.due && <DueBadge due={ads.due} receivedAt={receivedAt} />}
        {!running && frame?.kind !== 'lshape' && ads.lshapeDue && (
          <LShapeDueBadge due={ads.lshapeDue} receivedAt={receivedAt} />
        )}
        {running ? (
          <span className="deck__mode deck__mode--ad">
            Werbepause <SpotCountdown ad={ad} />
          </span>
        ) : frame ? (
          <span className="deck__mode deck__mode--ad">
            {frameLabel(frame)} <FrameCountdown frame={frame} receivedAt={receivedAt} />
          </span>
        ) : (
          <span className={`deck__mode${ads.logo.on && logos.length ? '' : ' deck__mode--off'}`}>
            {ads.logo.on && logos.length
              ? `Logo an · ${logos.length}`
              : ads.sponsors.length
                ? `${ads.sponsors.length} ${ads.sponsors.length === 1 ? 'Sponsor' : 'Sponsoren'}`
                : 'aus'}
          </span>
        )}
      </header>

      {/* Eine laufende Pause gehoert auch zugeklappt ins Blickfeld -- mit dem Griff zum Abbrechen. */}
      {running && (
        <div className="ad-running">
          <span>
            <b>{running.sponsorName}</b> läuft — noch <SpotCountdown ad={ad} />
            {output.muted && <em> · Ausgang stumm, Spot ohne Ton</em>}
          </span>
          <button type="button" className="btn btn--sm btn--onstage" onClick={() => send({ t: 'ads:break', materialId: null })}>
            Abbrechen
          </button>
        </div>
      )}
      {/* Banner und L gehoeren genauso ins Blickfeld -- sie laufen gern „bis Aus". */}
      {frame && (
        <div className="ad-running">
          <span>
            <b>{frameLabel(frame)}</b> im Bild
            {frame.durationMs !== null && (
              <>
                {' '}
                — noch <FrameCountdown frame={frame} receivedAt={receivedAt} />
              </>
            )}
            {ad.active && <em> · tritt zurück, solange {ad.active.clip ? 'die Botschaft' : 'die Werbepause'} läuft</em>}
          </span>
          <button type="button" className="btn btn--sm btn--onstage" onClick={() => send({ t: 'ads:frame', kind: null })}>
            Ausblenden
          </button>
          {frame.kind === 'lshape' && <LShapeRunning ads={ads} frame={frame} receivedAt={receivedAt} send={send} />}
        </div>
      )}

      {open && (
        <div className="deck__body">
          <div className="deck__block">
            <div className="deck__label">
              Sponsoren <span className="deck__count">{ads.sponsors.length}</span>
            </div>

            {ads.sponsors.length === 0 ? (
              <div className="deck__hint">
                Noch kein Sponsor. Lege unten einen an — mit Logo und, wenn es einen gibt, mit Werbespot. Weitere Bilder
                und Videos kommen danach beim Sponsor dazu.
              </div>
            ) : (
              <ul className="ad-sponsors">
                {ads.sponsors.map((sponsor) => (
                  <SponsorRow
                    key={sponsor.id}
                    sponsor={sponsor}
                    upload={uploads[sponsor.id]}
                    playingId={running?.materialId ?? null}
                    send={send}
                    onBeforeBreak={onBeforeBreak}
                    onImage={(file) => void addImage(sponsor.id, file)}
                    onVideo={(file) => void uploadVideo(sponsor.id, file)}
                    onRemoveMaterial={(material) => void removeMaterial(sponsor, material)}
                    onRemove={() => void remove(sponsor)}
                  />
                ))}
              </ul>
            )}

            <form className="ad-add" onSubmit={add} ref={formRef}>
              <input
                className="deck__input"
                placeholder="Neuer Sponsor, z. B. Autohaus Meyer"
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
              />
              <label className="ad-file">
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
                {logoFile ? `Logo: ${logoFile.name}` : 'Logo wählen'}
              </label>
              <label className="ad-file">
                <input type="file" accept="video/*" onChange={(e) => setSpotFile(e.target.files?.[0] ?? null)} />
                {spotFile ? `Spot: ${spotFile.name}` : 'Spot wählen (optional)'}
              </label>
              <button className="btn btn--sm" type="submit" disabled={busy || !name.trim()}>
                {busy ? 'Moment …' : 'Hinzufügen'}
              </button>
            </form>
            <div className="deck__hint">
              Bei jedem Bild und Video hakst du an, wofür es gedacht ist: Logo im Bild, Banner, L-Form oder Werbepause —
              auch mehreres. Logos am besten als PNG mit transparentem Hintergrund. Videos bis {MAX_SPOT_SECONDS / 60}{' '}
              Minuten, jedes gängige Format — der Server wandelt sie so um, dass sie auf jedem Handy und im
              YouTube-Ausgang laufen.
            </div>
            {error && <div className="notice notice--stage">{error}</div>}
          </div>

          <FrameBlock ads={ads} send={send} />

          <ScheduleBlock ads={ads} receivedAt={receivedAt} send={send} />

          <LShapeScheduleBlock ads={ads} receivedAt={receivedAt} send={send} />

          <LogoBlock ads={ads} logos={logos.length} send={send} />

          <div className="deck__block">
            <div className="deck__label">Live-Bild in der Werbepause</div>
            <CornerAndSize
              slot={ads.live.slot}
              size={ads.live.size}
              onSlot={(slot) => send({ t: 'ads:live', patch: { slot } })}
              onSize={(size) => send({ t: 'ads:live', patch: { size } })}
            />
            <div className="deck__hint">
              Während des Spots läuft das Live-Bild klein und stumm weiter — niemand verpasst etwas. Danach geht es von
              selbst zurück. Oben links steht „Werbung“ mit der Restzeit; die Kennzeichnung ist Pflicht und deshalb
              nicht abschaltbar.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function SponsorRow({
  sponsor,
  upload,
  playingId,
  send,
  onBeforeBreak,
  onImage,
  onVideo,
  onRemoveMaterial,
  onRemove,
}: {
  sponsor: Sponsor;
  upload: Upload | undefined;
  /** Das Video, das gerade als Werbepause laeuft -- egal von welchem Sponsor. */
  playingId: string | null;
  send: (msg: ClientMessage) => void;
  onBeforeBreak?: () => void;
  onImage: (file: File) => void;
  onVideo: (file: File) => void;
  onRemoveMaterial: (material: SponsorMaterial) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState(sponsor.name);
  useEffect(() => setDraft(sponsor.name), [sponsor.name]);
  const [tagline, setTagline] = useState(sponsor.tagline);
  useEffect(() => setTagline(sponsor.tagline), [sponsor.tagline]);
  const [cta, setCta] = useState(sponsor.cta);
  useEffect(() => setCta(sponsor.cta), [sponsor.cta]);
  const [link, setLink] = useState(sponsor.link);
  useEffect(() => setLink(sponsor.link), [sponsor.link]);
  const [linkError, setLinkError] = useState(false);
  const full = sponsor.materials.length >= MAX_MATERIALS;
  const inLShape = sponsor.materials.some((m) => m.uses.includes('lshape') && useFits('lshape', m.kind));

  const commitName = () => {
    const next = draft.replace(/\s+/g, ' ').trim();
    if (!next) return setDraft(sponsor.name);
    if (next !== sponsor.name) send({ t: 'ads:sponsor', sponsorId: sponsor.id, name: next });
  };

  // Leer ist erlaubt: dann stehen im Laufband nur die Bilder.
  const commitTagline = () => {
    const next = tagline.replace(/\s+/g, ' ').trim();
    if (next !== sponsor.tagline) send({ t: 'ads:sponsor', sponsorId: sponsor.id, tagline: next });
  };
  const commitCta = () => {
    const next = cta.replace(/\s+/g, ' ').trim();
    if (next !== sponsor.cta) send({ t: 'ads:sponsor', sponsorId: sponsor.id, cta: next });
  };
  // Was keine Web-Adresse ist, bleibt im Feld stehen und wird markiert -- nicht still verworfen.
  const commitLink = () => {
    const raw = link.trim();
    const next = raw ? normalizeLink(raw) : '';
    setLinkError(next === null);
    if (next !== null && next !== sponsor.link) send({ t: 'ads:sponsor', sponsorId: sponsor.id, link: next });
  };

  return (
    <li className={`ad-sponsor${sponsor.materials.some((m) => m.id === playingId) ? ' is-playing' : ''}`}>
      <div className="ad-sponsor__head">
        <input
          className="ad-sponsor__name"
          value={draft}
          maxLength={60}
          aria-label="Name des Sponsors"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <button type="button" className="deck__link deck__link--danger" onClick={onRemove}>
          Entfernen
        </button>
      </div>
      <input
        className="ad-sponsor__tagline"
        value={tagline}
        maxLength={MAX_TAGLINE}
        placeholder="Werbetext fürs Laufband und die L-Form (optional)"
        aria-label="Werbetext"
        onChange={(e) => setTagline(e.target.value)}
        onBlur={commitTagline}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      <div className="ad-sponsor__extra">
        <input
          className="ad-sponsor__tagline"
          value={cta}
          maxLength={MAX_CTA}
          placeholder="Hinweis in der L-Form, z. B. 10 % mit RABBIT10"
          aria-label="Hinweis in der L-Form"
          onChange={(e) => setCta(e.target.value)}
          onBlur={commitCta}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <input
          className={`ad-sponsor__tagline${linkError ? ' is-invalid' : ''}`}
          value={link}
          maxLength={MAX_LINK}
          inputMode="url"
          placeholder="Link für den QR-Code, z. B. rabbitfuel.de"
          aria-label="Link für den QR-Code"
          aria-invalid={linkError}
          onChange={(e) => {
            setLink(e.target.value);
            setLinkError(false);
          }}
          onBlur={commitLink}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <SponsorColor sponsor={sponsor} send={send} />
      </div>
      {linkError && <span className="ad-material__todo">Das ist keine Web-Adresse — z. B. rabbitfuel.de oder https://…</span>}
      {inLShape && !sponsor.tagline && !sponsor.cta && !sponsor.link && !sponsorLogoDistinct(sponsor) && (
        <span className="ad-sponsor__meta">
          Tipp: Mit Werbetext ist die L-Form unten gefüllt — sonst steht dort nur „Werbung“.
        </span>
      )}

      {sponsor.materials.length > 0 && (
        <ul className="ad-materials">
          {sponsor.materials.map((material) => (
            <MaterialRow
              key={material.id}
              sponsor={sponsor}
              material={material}
              playing={material.id === playingId}
              send={send}
              onBeforeBreak={onBeforeBreak}
              onRemove={() => onRemoveMaterial(material)}
            />
          ))}
        </ul>
      )}

      <div className="ad-sponsor__actions">
        <label className={`deck__link${full ? ' is-disabled' : ''}`}>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={full}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onImage(file);
            }}
          />
          + Bild
        </label>
        <label className={`deck__link${upload || full ? ' is-disabled' : ''}`}>
          <input
            type="file"
            accept="video/*"
            disabled={Boolean(upload) || full}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onVideo(file);
            }}
          />
          + Video
        </label>
        {upload && (
          <span className="ad-sponsor__meta">
            {upload.phase === 'upload' ? `Video lädt hoch … ${Math.round(upload.share * 100)} %` : 'Video wird umgewandelt …'}
          </span>
        )}
        {full && <span className="ad-sponsor__meta">Höchstens {MAX_MATERIALS} Stück je Sponsor.</span>}
      </div>
    </li>
  );
}

/**
 * Ob der Sponsor ein Logo hat, das nicht zugleich als Eckbild in der L-Form
 * laeuft -- nur dann steht es dort neben der Ecke, sonst sein Name.
 */
function sponsorLogoDistinct(sponsor: Sponsor): boolean {
  const logo = sponsorLogo(sponsor);
  return Boolean(
    logo &&
      !sponsor.materials.some(
        (m) => m.url === logo && m.uses.includes('lshape') && m.kind === 'image' && m.lshapeStyle === 'corner',
      ),
  );
}

/**
 * Farbe der L-Form: aus dem Logo oder eigene. Der Farbwaehler sendet erst,
 * wenn er kurz stillsteht -- jede Nachricht geht an alle im Raum.
 */
function SponsorColor({ sponsor, send }: { sponsor: Sponsor; send: (msg: ClientMessage) => void }) {
  const auto = useLogoColor(sponsor);
  const shown = sponsor.color ?? auto;
  const [draft, setDraft] = useState(shown ?? '#15181d');
  useEffect(() => setDraft(shown ?? '#15181d'), [shown]);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const change = (value: string) => {
    setDraft(value);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => send({ t: 'ads:sponsor', sponsorId: sponsor.id, color: value }), 250);
  };

  return (
    <div className="ad-color">
      <label className="ad-color__pick" title="Farbe der L-Form ändern">
        <input type="color" value={draft} onChange={(e) => change(e.target.value)} aria-label="Farbe der L-Form" />
        <span className="ad-color__swatch" style={{ background: shown ?? undefined }} data-empty={shown ? undefined : 'on'} />
      </label>
      <span className="ad-color__label">
        {sponsor.color ? 'Eigene Farbe' : auto ? 'Farbe aus dem Logo' : 'Logo ohne Farbe — L bleibt dunkel'}
      </span>
      {sponsor.color && (
        <button
          type="button"
          className="deck__link"
          onClick={() => {
            window.clearTimeout(timer.current);
            send({ t: 'ads:sponsor', sponsorId: sponsor.id, color: null });
          }}
        >
          aus dem Logo
        </button>
      )}
    </div>
  );
}

/**
 * Ein Bild oder Video mit seinen Haekchen. Angeboten wird nur, was zur Art
 * passt: ein Bild kann Logo, Banner und L-Form sein, ein Video L-Form und
 * Werbepause. Bei einem Bild in der L-Form kommt die Wahl dazu, ob es in der
 * Ecke steht oder als fertige L-Grafik beide Streifen fuellt.
 */
function MaterialRow({
  sponsor,
  material,
  playing,
  send,
  onBeforeBreak,
  onRemove,
}: {
  sponsor: Sponsor;
  material: SponsorMaterial;
  playing: boolean;
  send: (msg: ClientMessage) => void;
  onBeforeBreak?: () => void;
  onRemove: () => void;
}) {
  const uses = MATERIAL_USES.filter((u) => useFits(u.id, material.kind));
  const toggleUse = (use: MaterialUse) =>
    send({
      t: 'ads:material',
      sponsorId: sponsor.id,
      materialId: material.id,
      uses: material.uses.includes(use) ? material.uses.filter((u) => u !== use) : [...material.uses, use],
    });

  return (
    <li className={`ad-material${playing ? ' is-playing' : ''}${material.uses.length ? '' : ' is-unsorted'}`}>
      <div className="ad-material__thumb">
        {material.kind === 'image' ? (
          <img src={material.url} alt="" />
        ) : (
          <>
            <video src={`${material.url}#t=0.5`} preload="metadata" muted playsInline />
            <span className="ad-material__badge">▶ {formatSpotTime(material.durationMs ?? 0)}</span>
          </>
        )}
      </div>
      <div className="ad-material__main">
        <span className="ad-material__label" title={material.label}>
          {material.label}
        </span>
        <div className="ad-material__uses" role="group" aria-label={`Wofür ist „${material.label}" gedacht?`}>
          {uses.map((use) => (
            <button
              key={use.id}
              type="button"
              className={`chip chip--sm${material.uses.includes(use.id) ? ' is-active' : ''}`}
              aria-pressed={material.uses.includes(use.id)}
              title={use.hint}
              onClick={() => toggleUse(use.id)}
            >
              {material.uses.includes(use.id) ? '✓ ' : ''}
              {use.label}
            </button>
          ))}
        </div>
        {material.uses.length === 0 && <span className="ad-material__todo">Noch nicht eingeordnet — wofür ist es gedacht?</span>}
        {material.kind === 'image' && material.uses.includes('lshape') && (
          <div className="ad-material__style" role="group" aria-label="Bild in der L-Form">
            <span>In der L-Form als</span>
            {LSHAPE_IMAGE_STYLES.map((style) => (
              <button
                key={style.id}
                type="button"
                className={`chip chip--sm${material.lshapeStyle === style.id ? ' is-active' : ''}`}
                title={style.hint}
                onClick={() =>
                  send({ t: 'ads:material', sponsorId: sponsor.id, materialId: material.id, lshapeStyle: style.id })
                }
              >
                {style.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="ad-material__side">
        {material.kind === 'video' && material.uses.includes('break') && (
          <button
            type="button"
            className={`btn btn--sm${playing ? ' btn--onstage' : ''}`}
            title="Dieses Video jetzt bei allen als Werbepause abspielen"
            onClick={() => {
              if (!playing) onBeforeBreak?.();
              send({ t: 'ads:break', materialId: playing ? null : material.id });
            }}
          >
            {playing ? '■ Stopp' : '▶ Werbepause'}
          </button>
        )}
        <button type="button" className="deck__link deck__link--danger" onClick={onRemove}>
          Löschen
        </button>
      </div>
    </li>
  );
}

/**
 * Breite der L-Streifen im 1920 × 1080-Bild -- fuer den Hinweis, wie eine
 * L-Grafik anzulegen ist. Dieselben Anteile wie `--af-arm` im Stylesheet.
 */
const LSHAPE_ARM_PX: Record<AdFrameSize, { side: number; base: number }> = {
  sm: { side: Math.round(1920 * 0.22), base: Math.round(1080 * 0.22) },
  md: { side: Math.round(1920 * 0.28), base: Math.round(1080 * 0.28) },
  lg: { side: Math.round(1920 * 0.34), base: Math.round(1080 * 0.34) },
};

/** „Banner" oder „L-Form" -- fuer Kopfzeile und Leiste. */
const frameLabel = (frame: AdFrame) => (frame.kind === 'banner' ? 'Banner' : 'L-Form');

/** Restzeit von Banner oder L, die selbst tickt -- ohne feste Dauer steht nichts da. */
function FrameCountdown({ frame, receivedAt }: { frame: AdFrame; receivedAt: number }) {
  useTick(frame.durationMs !== null, 500);
  if (frame.durationMs === null) return null;
  const left = frame.durationMs - frame.elapsedMs - (performance.now() - receivedAt);
  return <span className="ad-countdown">{formatSpotTime(left)}</span>;
}

/** Drei Staerken als Knopfgruppe -- wie die Groessen bei Logo und kleinem Fenster. */
function FrameSizes({ size, onSize, label }: { size: AdFrameSize; onSize: (size: AdFrameSize) => void; label: string }) {
  return (
    <div className="deck-widgets__size" role="group" aria-label={label}>
      {AD_FRAME_SIZES.map((s) => (
        <button
          key={s.id}
          type="button"
          className={size === s.id ? 'is-on' : undefined}
          title={`${label}: ${s.label}`}
          onClick={() => onSize(s.id)}
        >
          {s.short}
        </button>
      ))}
    </div>
  );
}

/** Wie lange Banner oder L stehen -- `cycle`: bis alles, was in der L-Form laeuft, einmal dran war. */
type FrameLength = number | null | 'cycle';

/*
 * Dauer und Auswahl merkt sich der Browser, wie den offenen Block. Das
 * Tastenkuerzel Strg+Alt+L liest von hier -- es blendet ein, was im Block
 * gewaehlt ist.
 */
const LENGTH_KEY = 'rabbitfuel:regie-werbung-dauer';
const PICK_KEY = 'rabbitfuel:regie-lform-auswahl';

function readStored<T>(key: string, fallback: T, valid: (value: unknown) => value is T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const value: unknown = JSON.parse(raw);
    return valid(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function useStored<T>(key: string, fallback: T, valid: (value: unknown) => value is T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readStored(key, fallback, valid));
  const store = (next: T) => {
    setValue(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* Privater Modus -- dann eben nur bis zum Neuladen. */
    }
  };
  return [value, store];
}

const isLength = (value: unknown): value is FrameLength =>
  value === null || value === 'cycle' || AD_FRAME_DURATIONS.some((d) => d.ms === value);
const isPick = (value: unknown): value is string[] => Array.isArray(value) && value.every((v) => typeof v === 'string');

/** Was in der L-Form laeuft, wenn sie mit dieser Auswahl startet -- ohne passende Auswahl alles. */
function pickedLShape(ads: AdsState, pick: string[]): LShapeItem[] {
  const all = lshapeMaterials(ads);
  const chosen = all.filter((item) => pick.includes(item.sponsor.id));
  return chosen.length ? chosen : all;
}

function frameStart(ads: AdsState, kind: AdFrameKind, length: FrameLength, pick: string[]): ClientMessage {
  const durationMs = length === 'cycle' ? lshapeCycleMs(pickedLShape(ads, pick)) || null : length;
  return kind === 'lshape' ? { t: 'ads:frame', kind, durationMs, sponsorIds: pick } : { t: 'ads:frame', kind, durationMs };
}

/**
 * Strg+Alt+L: L-Form ein oder aus -- mit Dauer und Auswahl aus dem Block.
 * Null, wenn es nichts zu zeigen gibt.
 */
export function lshapeToggle(ads: AdsState): ClientMessage | null {
  if (ads.frame?.kind === 'lshape') return { t: 'ads:frame', kind: null };
  if (!lshapeMaterials(ads).length) return null;
  return frameStart(ads, 'lshape', readStored(LENGTH_KEY, null, isLength), readStored(PICK_KEY, [], isPick));
}

/** „Spot", „Bild" oder „L-Grafik" -- fuer die Anzeige, was gerade laeuft. */
const itemKind = (item: LShapeItem) =>
  item.material.kind === 'video' ? 'Spot' : item.material.lshapeStyle === 'full' ? 'L-Grafik' : 'Bild';

/**
 * Was in der laufenden L-Form gerade dran ist und was danach kommt -- mit
 * den Griffen „Weiter" und „Halten". Steht in der Leiste ueber dem Block,
 * also auch zugeklappt im Blickfeld.
 */
function LShapeRunning({
  ads,
  frame,
  receivedAt,
  send,
}: {
  ads: AdsState;
  frame: AdFrame;
  receivedAt: number;
  send: (msg: ClientMessage) => void;
}) {
  useTick(true, 500);
  const items = lshapeItems(ads, frame);
  const at = lshapeItemAt(items, lshapePosition(frame, receivedAt));
  if (!at) return null;
  const next = items.length > 1 ? items[(at.index + 1) % items.length] : null;
  return (
    <div className="ad-lshape-run">
      <span className="ad-lshape-run__now">
        {frame.held ? 'Gehalten' : 'Jetzt'}: <b>{at.item.sponsor.name}</b> · {itemKind(at.item)}
        {!frame.held && <span className="ad-countdown"> · noch {formatSpotTime(at.item.ms - at.offsetMs)}</span>}
        {next && <em> · danach {next.sponsor.name}</em>}
      </span>
      <span className="ad-lshape-run__actions">
        <button
          type="button"
          className="btn btn--sm btn--onstage"
          title="Sofort das nächste Stück der L-Form zeigen"
          onClick={() => send({ t: 'ads:lshapeRun', action: 'next' })}
        >
          ⏭ Weiter
        </button>
        <button
          type="button"
          className={`btn btn--sm${frame.held ? '' : ' btn--onstage'}`}
          aria-pressed={frame.held}
          title={frame.held ? 'Weiter reihum — es geht mit dem nächsten Stück weiter' : 'Dieses Stück stehen lassen — ein Spot läuft in Schleife'}
          onClick={() => send({ t: 'ads:lshapeRun', action: frame.held ? 'release' : 'hold' })}
        >
          {frame.held ? '▶ Weiterlaufen' : '⏸ Halten'}
        </button>
      </span>
    </div>
  );
}

/**
 * Vorschau der L-Form im Block: dieselben Bausteine wie im Stream, mit einem
 * Platzhalter statt des Live-Bilds. Sie laeuft reihum durch, was beim
 * naechsten Einblenden liefe -- Ecke, Breite und Look sieht die Regie, bevor
 * etwas auf Sendung geht.
 */
function LShapePreview({ ads, itemIds }: { ads: AdsState; itemIds: string[] }) {
  const key = itemIds.join(',');
  const [startedAt, setStartedAt] = useState(() => performance.now());
  useEffect(() => setStartedAt(performance.now()), [key]);
  const frame: AdFrame = {
    id: 'vorschau',
    kind: 'lshape',
    durationMs: null,
    elapsedMs: 0,
    items: itemIds,
    positionMs: 0,
    held: false,
  };
  const view: AdFrameView = {
    scene: { key: 'vorschau', frame, kind: 'lshape', banner: ads.banner, lshape: ads.lshape, receivedAt: startedAt },
    visible: true,
  };
  return (
    <div className="player ad-preview" {...adFrameChrome(view)} aria-label="Vorschau der L-Form">
      <div className="player__program">
        <div className="ad-preview__live">
          <span>
            <span className="dot dot--pulse" /> Live-Bild
          </span>
        </div>
      </div>
      <AdFrameLayer view={view} ads={ads} />
      <span className="ad-preview__label">Vorschau</span>
    </div>
  );
}

/**
 * Banner und L-Form: Werbung, ohne das Programm zu unterbrechen. Das
 * Live-Bild rueckt zusammen und bleibt zu hoeren; Banner oder L fahren in
 * den frei gewordenen Rand. Es laeuft immer nur eins von beiden.
 *
 * Lage, Drehen und Look gelten sofort -- auch fuer das, was gerade im Bild
 * steht. Dauer und Auswahl gelten fuers naechste Einblenden.
 */
function FrameBlock({ ads, send }: { ads: AdsState; send: (msg: ClientMessage) => void }) {
  const [length, setLength] = useStored<FrameLength>(LENGTH_KEY, null, isLength);
  const [storedPick, setPick] = useStored<string[]>(PICK_KEY, [], isPick);
  const { banner, lshape, frame } = ads;
  const allItems = lshapeMaterials(ads);
  const lshapeSponsors = [...new Map(allItems.map((item) => [item.sponsor.id, item.sponsor])).values()];
  const pick = storedPick.filter((id) => lshapeSponsors.some((sponsor) => sponsor.id === id));
  const picked = pickedLShape(ads, pick);
  const cycle = lshapeCycleMs(picked);
  const lshapeStills = picked.filter((item) => item.material.kind === 'image').length;
  const lshapeGraphics = allItems.filter((item) => item.material.kind === 'image' && item.material.lshapeStyle === 'full').length;
  const bannerReady = bannerHasContent(ads);
  const images = bannerImages(ads).length;
  const togglePick = (id: string) => setPick(pick.includes(id) ? pick.filter((p) => p !== id) : [...pick, id]);

  // Der Regler sendet erst, wenn er kurz stillsteht -- wie beim Logo-Wechsel.
  const [rotate, setRotate] = useState(banner.rotateSeconds);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => setRotate(banner.rotateSeconds), [banner.rotateSeconds]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const changeRotate = (value: number) => {
    setRotate(value);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => send({ t: 'ads:banner', patch: { rotateSeconds: value } }), 250);
  };
  const fill = ((rotate - ROTATE_LIMITS[0]) / (ROTATE_LIMITS[1] - ROTATE_LIMITS[0])) * 100;

  // Dasselbe fuer die Standzeit eines Bilds in der L-Form.
  const [still, setStill] = useState(lshape.imageSeconds);
  const stillTimer = useRef<number | undefined>(undefined);
  useEffect(() => setStill(lshape.imageSeconds), [lshape.imageSeconds]);
  useEffect(() => () => window.clearTimeout(stillTimer.current), []);
  const changeStill = (value: number) => {
    setStill(value);
    window.clearTimeout(stillTimer.current);
    stillTimer.current = window.setTimeout(() => send({ t: 'ads:lshape', patch: { imageSeconds: value } }), 250);
  };
  const stillFill = ((still - LSHAPE_IMAGE_LIMITS[0]) / (LSHAPE_IMAGE_LIMITS[1] - LSHAPE_IMAGE_LIMITS[0])) * 100;

  const toggle = (kind: AdFrameKind) =>
    send(frame?.kind === kind ? { t: 'ads:frame', kind: null } : frameStart(ads, kind, length, pick));

  return (
    <div className="deck__block">
      <div className="deck__label">Banner &amp; L-Form</div>
      <div className="ad-frame-start">
        <button
          type="button"
          className={`btn btn--sm${frame?.kind === 'banner' ? ' btn--onstage' : ''}`}
          disabled={!bannerReady && frame?.kind !== 'banner'}
          title={bannerReady ? 'Banner bei allen einblenden' : 'Erst bei einem Bild „Banner“ anhaken oder einen Werbetext eintragen'}
          onClick={() => toggle('banner')}
        >
          {frame?.kind === 'banner' ? '■ Banner aus' : '▶ Banner'}
        </button>
        <button
          type="button"
          className={`btn btn--sm${frame?.kind === 'lshape' ? ' btn--onstage' : ''}`}
          disabled={!allItems.length && frame?.kind !== 'lshape'}
          title={
            allItems.length
              ? `L-Form bei allen einblenden (${keyLabel('lshape')})`
              : 'Erst bei einem Bild oder Video „L-Form“ anhaken'
          }
          onClick={() => toggle('lshape')}
        >
          {frame?.kind === 'lshape' ? '■ L-Form aus' : '▶ L-Form'}
        </button>
      </div>
      <div className="chip-row" role="group" aria-label="Wie lange">
        {AD_FRAME_DURATIONS.map((option) => (
          <button
            key={option.label}
            type="button"
            className={`chip${length === option.ms ? ' is-active' : ''}`}
            onClick={() => setLength(option.ms)}
          >
            {option.label}
          </button>
        ))}
        {cycle > 0 && (
          <button
            type="button"
            className={`chip${length === 'cycle' ? ' is-active' : ''}`}
            title="So lange, bis alles, was in der L-Form läuft, einmal dran war"
            onClick={() => setLength('cycle')}
          >
            L-Form einmal ({formatSpotTime(cycle)})
          </button>
        )}
      </div>
      <div className="deck__hint">
        Das Live-Bild rückt zusammen und bleibt zu hören — die Werbung verdeckt nichts. Dauer und Auswahl gelten fürs
        nächste Einblenden; „bis Aus“ bleibt stehen, bis du ausblendest.
      </div>

      <div className="ad-frame-group">
        <span className="ad-frame-group__title">L-Form</span>
        {allItems.length > 0 && <LShapePreview ads={ads} itemIds={picked.map((item) => item.material.id)} />}
        <div className="chip-row">
          {LSHAPE_CORNERS.map((corner) => (
            <button
              key={corner.id}
              type="button"
              className={`chip${lshape.corner === corner.id ? ' is-active' : ''}`}
              onClick={() => send({ t: 'ads:lshape', patch: { corner: corner.id } })}
            >
              <span className="ad-frame-corner" aria-hidden>
                {corner.icon}
              </span>{' '}
              {corner.label}
            </button>
          ))}
          <FrameSizes size={lshape.size} label="Breite der L-Streifen" onSize={(size) => send({ t: 'ads:lshape', patch: { size } })} />
        </div>
        <div className="chip-row" role="group" aria-label="Farbe der L-Form">
          {AD_FRAME_LOOKS.map((look) => (
            <button
              key={look.id}
              type="button"
              className={`chip${lshape.look === look.id ? ' is-active' : ''}`}
              title={look.hint}
              onClick={() => send({ t: 'ads:lshape', patch: { look: look.id } })}
            >
              {look.label}
            </button>
          ))}
        </div>
        {lshapeSponsors.length > 1 && (
          <div className="chip-row" role="group" aria-label="Wer in der L-Form läuft">
            <span className="ad-frame-group__sub">Läuft:</span>
            <button type="button" className={`chip chip--sm${pick.length ? '' : ' is-active'}`} onClick={() => setPick([])}>
              Alle
            </button>
            {lshapeSponsors.map((sponsor) => (
              <button
                key={sponsor.id}
                type="button"
                className={`chip chip--sm${pick.includes(sponsor.id) ? ' is-active' : ''}`}
                aria-pressed={pick.includes(sponsor.id)}
                onClick={() => togglePick(sponsor.id)}
              >
                {pick.includes(sponsor.id) ? '✓ ' : ''}
                {sponsor.name}
              </button>
            ))}
          </div>
        )}
        {lshapeStills > 0 && (
          <label className="deck-slider">
            <span>Bild steht</span>
            <input
              type="range"
              min={LSHAPE_IMAGE_LIMITS[0]}
              max={LSHAPE_IMAGE_LIMITS[1]}
              step={1}
              value={still}
              style={{ ['--fill' as string]: `${stillFill}%` }}
              onChange={(e) => changeStill(Number(e.target.value))}
            />
            <b>{still} s</b>
          </label>
        )}
        <div className="deck__hint">
          {allItems.length
            ? `${picked.length > 1 ? `${picked.length} Stück laufen reihum. ` : ''}Videos laufen stumm in der Ecke, Eckbilder stehen dort — daneben Logo, Werbetext, Hinweis und QR-Code. Während sie läuft: „Weiter“ und „Halten“ oben in der Leiste.`
            : 'Hake bei einem Bild oder Video „L-Form“ an — Videos laufen stumm in der Ecke, Bilder stehen dort oder füllen als L-Grafik beide Streifen.'}
        </div>
        {lshapeGraphics > 0 && (
          <div className="deck__hint">
            L-Grafik: 1920 × 1080 anlegen. Sichtbar sind nur die Streifen — bei Breite {AD_FRAME_SIZES.find((s) => s.id === lshape.size)?.short}{' '}
            {LSHAPE_ARM_PX[lshape.size].side} px an der Seite und {LSHAPE_ARM_PX[lshape.size].base} px an der Kante ({LSHAPE_CORNERS.find((c) => c.id === lshape.corner)?.label}); der Rest liegt unter dem Live-Bild.
          </div>
        )}
      </div>

      <div className="ad-frame-group">
        <span className="ad-frame-group__title">Banner</span>
        <div className="chip-row">
          {BANNER_EDGES.map((edge) => (
            <button
              key={edge.id}
              type="button"
              className={`chip${banner.edge === edge.id ? ' is-active' : ''}`}
              onClick={() => send({ t: 'ads:banner', patch: { edge: edge.id } })}
            >
              {edge.label}
            </button>
          ))}
          {BANNER_STYLES.map((style) => (
            <button
              key={style.id}
              type="button"
              className={`chip${banner.style === style.id ? ' is-active' : ''}`}
              title={style.hint}
              onClick={() => send({ t: 'ads:banner', patch: { style: style.id } })}
            >
              {style.label}
            </button>
          ))}
          <FrameSizes size={banner.size} label="Höhe des Banners" onSize={(size) => send({ t: 'ads:banner', patch: { size } })} />
        </div>
        <div className="chip-row" role="group" aria-label="Fläche des Banners">
          {AD_FRAME_LOOKS.filter((look) => look.id !== 'sponsor').map((look) => (
            <button
              key={look.id}
              type="button"
              className={`chip${banner.look === look.id ? ' is-active' : ''}`}
              title={look.hint}
              onClick={() => send({ t: 'ads:banner', patch: { look: look.id as 'dark' | 'light' } })}
            >
              {look.label}
            </button>
          ))}
        </div>
        {banner.style === 'images' && images === 0 && (
          <div className="notice notice--stage">
            Noch kein Bild mit Häkchen „Banner“ — bis dahin laufen die Werbetexte durch.
          </div>
        )}
        {banner.style === 'images' && images > 1 && (
          <label className="deck-slider">
            <span>Wechsel alle</span>
            <input
              type="range"
              min={ROTATE_LIMITS[0]}
              max={ROTATE_LIMITS[1]}
              step={1}
              value={rotate}
              style={{ ['--fill' as string]: `${fill}%` }}
              onChange={(e) => changeRotate(Number(e.target.value))}
            />
            <b>{rotate} s</b>
          </label>
        )}
        <div className="deck__hint">
          {banner.style === 'crawl'
            ? 'Im Laufband rollen die Bilder mit Häkchen „Banner“, der Werbetext und der Hinweis jedes Sponsors durch.'
            : 'Die Bilder mit Häkchen „Banner“ wechseln sich ab — am besten im Format 8 : 1, z. B. 1920 × 240 Pixel.'}
        </div>
      </div>
    </div>
  );
}

/** Sendezeit bis zur naechsten L-Form nach Plan -- wie `dueLeft`. */
function lshapeDueLeft(due: AdLShapeDue, receivedAt: number): number {
  return due.counting ? Math.max(0, due.inMs - (performance.now() - receivedAt)) : due.inMs;
}

const LSHAPE_MODE_HINTS: Record<AdScheduleMode, string> = {
  off: 'L-Form nur von Hand.',
  auto: 'Die L-Form blendet sich von selbst ein und wieder aus. 30 Sekunden vorher kannst du sie noch schieben oder auslassen.',
  confirm: 'Ist eine L-Form fällig, fragt die Regie nach und wartet, bis du sie freigibst.',
};

/**
 * L-Form nach Zeitplan: eigener Takt neben den Werbepausen. Gezaehlt wird
 * Sendezeit, solange keine L-Form steht -- jede, auch eine von Hand, faengt
 * den Plan von vorn an. Es laeuft alles mit Haekchen „L-Form".
 */
function LShapeScheduleBlock({
  ads,
  receivedAt,
  send,
}: {
  ads: AdsState;
  receivedAt: number;
  send: (msg: ClientMessage) => void;
}) {
  const { lshapeSchedule: schedule, lshapeDue: due } = ads;
  const [every, setEvery] = useState(String(schedule.everyMinutes));
  useEffect(() => setEvery(String(schedule.everyMinutes)), [schedule.everyMinutes]);
  useTick(Boolean(due?.counting), 1000);

  const parsed = Number(every.trim().replace(',', '.'));
  const valid = Number.isFinite(parsed) && parsed >= AD_EVERY_LIMITS[0] && parsed <= AD_EVERY_LIMITS[1];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) send({ t: 'ads:lshapeSchedule', patch: { everyMinutes: Math.round(parsed) } });
  };
  const ready = lshapeMaterials(ads).length > 0;
  const cycle = lshapeCycleMs(lshapeMaterials(ads));

  return (
    <div className="deck__block">
      <div className="deck__label">L-Form nach Zeitplan</div>
      <div className="chip-row">
        {AD_SCHEDULE_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`chip${schedule.mode === m.id ? ' is-active' : ''}`}
            title={LSHAPE_MODE_HINTS[m.id]}
            onClick={() => send({ t: 'ads:lshapeSchedule', patch: { mode: m.id } })}
          >
            {m.label}
          </button>
        ))}
      </div>

      {schedule.mode !== 'off' && (
        <>
          <div className="chip-row">
            {LSHAPE_EVERY_PRESETS.map((minutes) => (
              <button
                key={minutes}
                type="button"
                className={`chip${schedule.everyMinutes === minutes ? ' is-active' : ''}`}
                onClick={() => send({ t: 'ads:lshapeSchedule', patch: { everyMinutes: minutes } })}
              >
                alle {minutes} min
              </button>
            ))}
          </div>
          <form className="ad-every" onSubmit={submit}>
            <label htmlFor="lshape-every">Abstand</label>
            <input
              id="lshape-every"
              value={every}
              onChange={(e) => setEvery(e.target.value)}
              inputMode="numeric"
              aria-describedby="lshape-every-unit"
            />
            <span id="lshape-every-unit" className="deck__hint">
              min
            </span>
            <button className="btn btn--sm" type="submit" disabled={!valid || Math.round(parsed) === schedule.everyMinutes}>
              Setzen
            </button>
          </form>
          <div className="chip-row" role="group" aria-label="Wie lange sie dann steht">
            <span className="ad-frame-group__sub">Steht:</span>
            {LSHAPE_FOR_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                className={`chip${schedule.forMs === option.value ? ' is-active' : ''}`}
                onClick={() => send({ t: 'ads:lshapeSchedule', patch: { forMs: option.value } })}
              >
                {option.value === 'cycle' && cycle > 0 ? `${option.label} (${formatSpotTime(cycle)})` : option.label}
              </button>
            ))}
          </div>
          {!ready ? (
            <div className="notice notice--stage">
              Noch nichts mit Häkchen „L-Form“ — sonst hat der Zeitplan nichts zu zeigen.
            </div>
          ) : (
            due && (
              <div className="ad-plan">
                {due.waiting ? (
                  <b>L-Form fällig — wartet auf deine Freigabe.</b>
                ) : (
                  <span>
                    Nächste L-Form in <b className="ad-countdown">{formatSpotTime(lshapeDueLeft(due, receivedAt))}</b>{' '}
                    Sendezeit
                    {!due.counting && (
                      <em> · steht, {ads.frame?.kind === 'lshape' ? 'solange eine L-Form läuft' : 'solange nichts auf Sendung ist'}</em>
                    )}
                  </span>
                )}
                <span>steht {formatSpotTime(due.durationMs)}</span>
              </div>
            )
          )}
        </>
      )}
      <div className="deck__hint">
        {LSHAPE_MODE_HINTS[schedule.mode]} Läuft alles mit Häkchen „L-Form“ — unabhängig vom Zeitplan der Werbepausen.
      </div>
    </div>
  );
}

/**
 * Werbung nach Zeitplan: Modus und Abstand. Gezaehlt wird Sendezeit -- ohne
 * Hauptbild auf Sendung steht der Plan, und jede Pause, auch eine von Hand,
 * faengt ihn von vorn an. Die Spots kommen reihum dran.
 */
function ScheduleBlock({
  ads,
  receivedAt,
  send,
}: {
  ads: AdsState;
  receivedAt: number;
  send: (msg: ClientMessage) => void;
}) {
  const { schedule, due } = ads;
  const [every, setEvery] = useState(String(schedule.everyMinutes));
  useEffect(() => setEvery(String(schedule.everyMinutes)), [schedule.everyMinutes]);
  useTick(Boolean(due?.counting), 1000);

  const parsed = Number(every.trim().replace(',', '.'));
  const valid = Number.isFinite(parsed) && parsed >= AD_EVERY_LIMITS[0] && parsed <= AD_EVERY_LIMITS[1];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) send({ t: 'ads:schedule', patch: { everyMinutes: Math.round(parsed) } });
  };
  const hasSpot = breakSpots(ads).length > 0;
  const mode = AD_SCHEDULE_MODES.find((m) => m.id === schedule.mode);

  return (
    <div className="deck__block">
      <div className="deck__label">Werbepausen nach Zeitplan</div>
      <div className="chip-row">
        {AD_SCHEDULE_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`chip${schedule.mode === m.id ? ' is-active' : ''}`}
            title={m.hint}
            onClick={() => send({ t: 'ads:schedule', patch: { mode: m.id } })}
          >
            {m.label}
          </button>
        ))}
      </div>

      {schedule.mode !== 'off' && (
        <>
          <div className="chip-row">
            {AD_EVERY_PRESETS.map((minutes) => (
              <button
                key={minutes}
                type="button"
                className={`chip${schedule.everyMinutes === minutes ? ' is-active' : ''}`}
                onClick={() => send({ t: 'ads:schedule', patch: { everyMinutes: minutes } })}
              >
                alle {minutes} min
              </button>
            ))}
          </div>
          <form className="ad-every" onSubmit={submit}>
            <label htmlFor="ad-every">Abstand</label>
            <input
              id="ad-every"
              value={every}
              onChange={(e) => setEvery(e.target.value)}
              inputMode="numeric"
              aria-describedby="ad-every-unit"
            />
            <span id="ad-every-unit" className="deck__hint">
              min
            </span>
            <button className="btn btn--sm" type="submit" disabled={!valid || Math.round(parsed) === schedule.everyMinutes}>
              Setzen
            </button>
          </form>
          {!hasSpot ? (
            <div className="notice notice--stage">
              Noch kein Video mit Häkchen „Werbepause“ — sonst hat der Zeitplan nichts zu zeigen.
            </div>
          ) : (
            due && (
              <div className="ad-plan">
                {due.waiting ? (
                  <b>Werbung fällig — wartet auf deine Freigabe.</b>
                ) : (
                  <span>
                    Nächste Pause in <b className="ad-countdown">{formatSpotTime(dueLeft(due, receivedAt))}</b>{' '}
                    Sendezeit
                    {!due.counting && <em> · steht, solange nichts auf Sendung ist</em>}
                  </span>
                )}
                <span>Spot: {due.sponsorName}</span>
              </div>
            )
          )}
        </>
      )}
      {mode && <div className="deck__hint">{mode.hint}</div>}
    </div>
  );
}

/** Kleine Anzeige im Kopf des Werbe-Blocks: wann die naechste Pause nach Plan kommt. */
function DueBadge({ due, receivedAt }: { due: AdDue; receivedAt: number }) {
  useTick(due.counting, 1000);
  return (
    <span className={`deck__mode deck__mode--plan${due.waiting ? ' is-due' : ''}`}>
      {due.waiting ? 'Werbung fällig' : `Werbung in ${formatSpotTime(dueLeft(due, receivedAt))}`}
    </span>
  );
}

/** Dasselbe fuer die L-Form nach Plan. */
function LShapeDueBadge({ due, receivedAt }: { due: AdLShapeDue; receivedAt: number }) {
  useTick(due.counting, 1000);
  return (
    <span className={`deck__mode deck__mode--plan${due.waiting ? ' is-due' : ''}`}>
      {due.waiting ? 'L-Form fällig' : `L-Form in ${formatSpotTime(lshapeDueLeft(due, receivedAt))}`}
    </span>
  );
}

/**
 * Hinweis auf die naechste Pause nach Plan -- steht ueber allem in der Regie,
 * damit er auch im Chat-Reiter am Handy nicht untergeht.
 *
 * Automatisch: eine halbe Minute Vorwarnung, dann startet die Pause von
 * selbst. Mit Hinweis: faellig ist faellig, aber los geht es erst, wenn die
 * Regie freigibt -- vorher kann sie ansagen, dass es gleich weitergeht, oder
 * eine spannende Szene abwarten.
 */
export function AdDuePrompt({
  ads,
  receivedAt,
  running,
  send,
  onBeforeBreak,
}: {
  ads: AdsState;
  receivedAt: number;
  /** Laeuft schon eine Pause, gibt es nichts anzukuendigen. */
  running: boolean;
  send: (msg: ClientMessage) => void;
  onBeforeBreak?: () => void;
}) {
  const { due, schedule, lshapeDue, lshapeSchedule } = ads;
  useTick(Boolean((schedule.mode === 'auto' && due?.counting) || (lshapeSchedule.mode === 'auto' && lshapeDue?.counting)), 250);
  if (running) return null;

  const breakAuto = schedule.mode === 'auto';
  const breakLeft = due ? dueLeft(due, receivedAt) : 0;
  const showBreak = Boolean(due) && (breakAuto ? breakLeft <= AD_WARN_MS : schedule.mode === 'confirm' && due!.waiting);

  const lshapeAuto = lshapeSchedule.mode === 'auto';
  const lshapeLeft = lshapeDue ? lshapeDueLeft(lshapeDue, receivedAt) : 0;
  const showLShape =
    Boolean(lshapeDue) &&
    ads.frame?.kind !== 'lshape' &&
    (lshapeAuto ? lshapeLeft <= AD_WARN_MS : lshapeSchedule.mode === 'confirm' && lshapeDue!.waiting);

  if (!showBreak && !showLShape) return null;

  const answer = (action: 'now' | 'snooze' | 'skip') => {
    if (action === 'now') onBeforeBreak?.();
    send({ t: 'ads:due', action });
  };
  const answerLShape = (action: 'now' | 'snooze' | 'skip') => send({ t: 'ads:lshapeDue', action });

  return (
    <div className="ad-prompts">
      {showBreak && due && (
        <div className="ad-prompt" role="alertdialog" aria-labelledby="ad-prompt-title" aria-describedby="ad-prompt-text">
          <div className="ad-prompt__head">
            <b id="ad-prompt-title">
              {breakAuto ? (
                <>
                  Werbung in <span className="ad-countdown">{formatSpotTime(breakLeft)}</span>
                </>
              ) : (
                'Werbepause fällig'
              )}
            </b>
            <span>
              {due.sponsorName}
              {due.durationMs ? ` · ${formatSpotTime(due.durationMs)}` : ''}
            </span>
          </div>
          <p id="ad-prompt-text">
            {breakAuto
              ? 'Sag kurz an, dass es gleich weitergeht — oder schieb die Pause, wenn es gerade spannend ist.'
              : 'Sag kurz an, z. B. „Nach einer kurzen Unterbrechung geht es weiter“ — und gib die Werbung dann frei.'}
            {breakAuto && !due.counting && ' Steht, solange nichts auf Sendung ist.'}
          </p>
          <div className="ad-prompt__actions">
            <button type="button" className="btn btn--sm" onClick={() => answer('now')}>
              {breakAuto ? 'Jetzt starten' : 'Werbung starten'}
            </button>
            <button type="button" className="btn btn--sm btn--onstage" onClick={() => answer('snooze')}>
              {AD_SNOOZE_MS / 60_000} min später
            </button>
            <button type="button" className="btn btn--sm btn--onstage" onClick={() => answer('skip')}>
              Diesmal auslassen
            </button>
          </div>
        </div>
      )}
      {showLShape && lshapeDue && (
        <div
          className="ad-prompt ad-prompt--lshape"
          role="alertdialog"
          aria-labelledby="lshape-prompt-title"
          aria-describedby="lshape-prompt-text"
        >
          <div className="ad-prompt__head">
            <b id="lshape-prompt-title">
              {lshapeAuto ? (
                <>
                  L-Form in <span className="ad-countdown">{formatSpotTime(lshapeLeft)}</span>
                </>
              ) : (
                'L-Form fällig'
              )}
            </b>
            <span>steht {formatSpotTime(lshapeDue.durationMs)}</span>
          </div>
          <p id="lshape-prompt-text">
            {lshapeAuto
              ? 'Das Live-Bild rückt gleich zusammen und bleibt zu hören. Schieb die L-Form, wenn es gerade spannend ist.'
              : 'Gib die L-Form frei, wenn es passt — das Live-Bild rückt zusammen und bleibt zu hören.'}
            {ads.frame?.kind === 'banner' && ' Das Banner macht dafür Platz.'}
            {lshapeAuto && !lshapeDue.counting && ' Steht, solange nichts auf Sendung ist.'}
          </p>
          <div className="ad-prompt__actions">
            <button type="button" className="btn btn--sm" onClick={() => answerLShape('now')}>
              {lshapeAuto ? 'Jetzt einblenden' : 'L-Form einblenden'}
            </button>
            <button type="button" className="btn btn--sm btn--onstage" onClick={() => answerLShape('snooze')}>
              {AD_SNOOZE_MS / 60_000} min später
            </button>
            <button type="button" className="btn btn--sm btn--onstage" onClick={() => answerLShape('skip')}>
              Diesmal auslassen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LogoBlock({ ads, logos, send }: { ads: AdsState; logos: number; send: (msg: ClientMessage) => void }) {
  const logo = ads.logo;
  // Der Regler sendet erst, wenn er kurz stillsteht -- jede Nachricht geht an alle im Raum.
  const [rotate, setRotate] = useState(logo.rotateSeconds);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => setRotate(logo.rotateSeconds), [logo.rotateSeconds]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const changeRotate = (value: number) => {
    setRotate(value);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => send({ t: 'ads:logo', patch: { rotateSeconds: value } }), 250);
  };
  const fill = ((rotate - ROTATE_LIMITS[0]) / (ROTATE_LIMITS[1] - ROTATE_LIMITS[0])) * 100;

  return (
    <div className="deck__block">
      <div className="deck__label">
        Logo im Bild
        <button
          type="button"
          className={`deck-widgets__on ad-logo-switch${logo.on ? ' is-on' : ''}`}
          onClick={() => send({ t: 'ads:logo', patch: { on: !logo.on } })}
          aria-pressed={logo.on}
        >
          {logo.on ? 'an' : 'aus'}
        </button>
      </div>
      {logo.on && logos === 0 && (
        <div className="notice notice--stage">
          Kein Logo im Bild: Hake bei einem Bild „Logo im Bild“ an.
        </div>
      )}
      <CornerAndSize
        slot={logo.slot}
        size={logo.size}
        onSlot={(slot) => send({ t: 'ads:logo', patch: { slot } })}
        onSize={(size) => send({ t: 'ads:logo', patch: { size } })}
      />
      <div className="chip-row">
        {SPONSOR_PLATES.map((plate) => (
          <button
            key={plate.id}
            type="button"
            className={`chip${logo.plate === plate.id ? ' is-active' : ''}`}
            title={plate.hint}
            onClick={() => send({ t: 'ads:logo', patch: { plate: plate.id } })}
          >
            Fläche: {plate.label}
          </button>
        ))}
      </div>
      {logos > 1 && (
        <label className="deck-slider">
          <span>Wechsel alle</span>
          <input
            type="range"
            min={ROTATE_LIMITS[0]}
            max={ROTATE_LIMITS[1]}
            step={1}
            value={rotate}
            style={{ ['--fill' as string]: `${fill}%` }}
            onChange={(e) => changeRotate(Number(e.target.value))}
          />
          <b>{rotate} s</b>
        </label>
      )}
      <div className="deck__hint">
        {logos > 1
          ? `${logos} Logos wechseln sich ab — bei allen Zuschauern gleichzeitig.`
          : 'Das Logo steht in der gewählten Ecke und weicht Einblendungen und kleinem Fenster aus. Zuschauer können es nicht ausblenden.'}
      </div>
    </div>
  );
}

/** Ecke und Groesse -- dieselbe Bedienung wie beim kleinen Fenster unter dem Monitor. */
function CornerAndSize({
  slot,
  size,
  onSlot,
  onSize,
}: {
  slot: CornerSlot;
  size: WidgetSize;
  onSlot: (slot: CornerSlot) => void;
  onSize: (size: WidgetSize) => void;
}) {
  return (
    <div className="ad-layout">
      <div className="chip-row">
        {CORNER_SLOTS.map((corner) => (
          <button
            key={corner.id}
            type="button"
            className={`chip${slot === corner.id ? ' is-active' : ''}`}
            onClick={() => onSlot(corner.id)}
          >
            {corner.label}
          </button>
        ))}
      </div>
      <div className="deck-widgets__size" role="group" aria-label="Größe">
        {WIDGET_SIZES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={size === s.id ? 'is-on' : undefined}
            title={s.label}
            onClick={() => onSize(s.id)}
          >
            {s.short}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Restzeit, die selbst tickt -- das Pult rendert dafuer nicht neu. */
function SpotCountdown({ ad }: { ad: AdBreakView }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!ad.active) return;
    const timer = window.setInterval(() => tick((n) => n + 1), 250);
    return () => window.clearInterval(timer);
  }, [ad.active]);
  if (!ad.active) return null;
  return <span className="ad-countdown">{formatSpotTime(ad.active.durationMs - ad.position())}</span>;
}
